import LogoSVG from '@assets/player/logo-shadow.svg';
import classNames from 'classnames';
import { type FC, useEffect, useState } from 'react';
import { trackEvent } from 'src/lib/googleAnalytics';
import ControlBar from '../components/Player/ControlBar';
import InfoSheet, { useAvailableInfoSheetTabs } from '../components/Player/InfoSheet';
import MapView from '../components/Player/Map';
import { SidebarButton } from '../components/Player/PlayerSidebar';
import {
  PlayerSidebarWrapper,
  SIDEBAR_WIDTH_BREAKPOINT,
} from '../components/Player/PlayerSidebarWrapper';
import { ShareButton } from '../components/Player/Share/ShareButton';
import { SponsorLogos } from '../components/Player/SponsorLogos/SponsorLogos';
import { EventReloadManager } from '../lib/player/controller/EventReloadManager';
import { FavoritesManager } from '../lib/player/controller/FavoritesManager';
import { HashManager } from '../lib/player/controller/HashManager';
import { KeyPressManager } from '../lib/player/controller/KeyPressManager';
import { GroupLiveMotionManager } from '../lib/player/controller/LiveMotionManager';
import { OwnLocationManager } from '../lib/player/controller/OwnLocationManager';
import { PlaybackPositionManager } from '../lib/player/controller/PlaybackPositionManager';
import { GroupStarterTrackManager } from '../lib/player/controller/StarterTrackManager';
import { Timer } from '../lib/player/controller/Timer';
import { triggerMapLoad } from '../lib/player/model';
import { getCurrentEvents, reduceStartTime } from '../lib/player/selectors';
import type { EventState } from '../lib/player/store';
import { useStore } from '../lib/player/store';
import { correctedNow } from '../lib/player/utils/clock';
import { useMeasure, useOncePerEvent } from '../lib/player/utils/hooks';

type Bounds = ReturnType<typeof useMeasure>[1];

export const PlayerView: FC = () => {
  const [visibleMapRef, visibleMapBounds] = useMeasure({
    debounce: 100,
    scroll: false,
  });
  const [mapContainerRef, mapContainerBounds] = useMeasure({
    debounce: 100,
    scroll: false,
  });

  const currentEvent1 = useStore((state) => getCurrentEvents(state)).at(0);
  // `#mapOnly=true`: nothing but the map, for TV productions and LED walls. Only the Mapbox
  // attribution and the Racemap logo stay.
  const mapOnly = useStore((state) => state.options.mapOnly);

  const infoSheetEvent = useInfoSheetEvent(currentEvent1, mapOnly);
  const [showSidebar, setShowSidebar] = useState(
    () =>
      !mapOnly &&
      window.innerWidth > SIDEBAR_WIDTH_BREAKPOINT &&
      !!currentEvent1?.options.listOpenByDefault,
  );
  const mapViewIsInitialized = useMapViewIsInitialized(
    visibleMapBounds,
    mapContainerBounds,
    infoSheetEvent != null,
  );
  const eventId = useStore((state) => state.eventId);
  useOncePerEvent(eventId, triggerMapLoad);

  const hasMapHeight = mapContainerBounds.height > 0;

  return (
    <div className={classNames('inner', { 'side-bar-open': showSidebar })}>
      <PlayerControllers />
      <div className="map-container">
        {!mapOnly && hasMapHeight && (
          <PlayerSidebarWrapper
            closed={!showSidebar}
            onClose={() => {
              setShowSidebar(false);
            }}
          />
        )}
        <div className="map-container-sizer" ref={mapContainerRef}>
          {mapViewIsInitialized && (
            <MapView mapBounds={mapContainerBounds} visibleMapBounds={visibleMapBounds} />
          )}

          {hasMapHeight && (
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
              <div
                style={{ flex: 1, position: 'relative', pointerEvents: 'none' }}
                ref={visibleMapRef}
              >
                {visibleMapBounds.width > 0 && (
                  <MapOverlay
                    mapOnly={mapOnly}
                    showSidebar={showSidebar}
                    onToggleSidebar={() => setShowSidebar((v) => !v)}
                  />
                )}
              </div>
              {infoSheetEvent != null && (
                <InfoSheet
                  maxHeight={mapContainerBounds.height}
                  isInitiallyOpen={infoSheetEvent.options.infoTabs.openByDefault}
                />
              )}
            </div>
          )}
        </div>
      </div>
      {!mapOnly && <ControlBar className="control-bar" />}
    </div>
  );
};

// The event whose info sheet is shown, or null when it has no tab to show.
function useInfoSheetEvent(currentEvent: EventState | undefined, mapOnly: boolean) {
  const availableTabs = useAvailableInfoSheetTabs(currentEvent ?? null);
  const hasTabs = Object.values(availableTabs).some((a) => a);
  return currentEvent != null && hasTabs && !mapOnly ? currentEvent : null;
}

// True once both containers are measured; the map must not mount at a zero size.
function useMapViewIsInitialized(
  visibleMapBounds: Bounds,
  mapContainerBounds: Bounds,
  showsInfoSheet: boolean,
) {
  const [isInitialized, setIsInitialized] = useState(false);

  useEffect(() => {
    if (
      visibleMapBounds.width > 0 &&
      mapContainerBounds.height > 0 &&
      (visibleMapBounds.height !== mapContainerBounds.height || !showsInfoSheet)
    ) {
      setIsInitialized(true);
    }
  }, [visibleMapBounds, mapContainerBounds, showsInfoSheet]);

  return isInitialized && visibleMapBounds.width > 0 && mapContainerBounds.height > 0;
}

const PlayerControllers: FC = () => {
  const isPlaying = useStore((state) => state.timer.isPlaying);
  const currentEvents = useStore((state) => getCurrentEvents(state));
  const startTime = reduceStartTime(currentEvents);

  return (
    <>
      {isPlaying && <Timer interval={50} />}
      {startTime <= correctedNow() && <KeyPressManager />}
      <GroupLiveMotionManager />
      <GroupStarterTrackManager />
      <HashManager />
      <FavoritesManager />
      <PlaybackPositionManager />
      <OwnLocationManager />
      <EventReloadManager />
    </>
  );
};

function handleRacemapLogoClick() {
  trackEvent('logo_click', { logo: 'racemap' });
}

const MapOverlay: FC<{ mapOnly: boolean; showSidebar: boolean; onToggleSidebar: () => void }> = ({
  mapOnly,
  showSidebar,
  onToggleSidebar,
}) => (
  <>
    <div className="logos" style={{ pointerEvents: 'all' }}>
      {/* The only Mapbox wordmark on screen: mapbox-gl's own control is hidden in
          player.scss because the map element reaches under the info sheet and the
          control bar. Keep it visible and linking to mapbox.com — Mapbox's terms
          require the attribution. */}
      {/* biome-ignore lint/a11y/useAnchorContent: mapbox inject that info */}
      <a
        href="https://mapbox.com/"
        className="mapboxgl-ctrl-logo"
        target="_blank"
        rel="noopener noreferrer"
      />
      {/* Wording and links are prescribed by Mapbox, so they stay untranslated. */}
      <div className="map-attribution">
        <a href="https://www.mapbox.com/about/maps/" target="_blank" rel="noopener noreferrer">
          © Mapbox
        </a>{' '}
        <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noopener noreferrer">
          © OpenStreetMap
        </a>{' '}
        <a href="https://apps.mapbox.com/feedback/" target="_blank" rel="noopener noreferrer">
          Improve this map
        </a>
      </div>
      <a
        href="https://racemap.com/"
        title="» Racemap"
        className="link-button"
        target="_blank"
        rel="noopener noreferrer"
        onClick={handleRacemapLogoClick}
      >
        <img src={LogoSVG} alt="» Racemap" />
      </a>
      {!mapOnly && <SponsorLogos />}
    </div>
    {!mapOnly && (
      <>
        <SidebarButton isOpen={showSidebar} onToggle={onToggleSidebar} />
        <ShareButton />
      </>
    )}
  </>
);
